import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, of } from 'rxjs';

export class TranslateExtension {
  constructor(private translate: TranslateService) { }

  /**
   * Creates a safe translation stream that handles interpolation errors gracefully.
   * This prevents "Cannot read properties of undefined" errors when translation keys
   * reference variables like {{ error.error }} that may be undefined.
   *
   * The issue occurs when MessageFormat tries to compile translation keys that reference
   * nested error properties. By providing default values for common variables, we ensure
   * compilation succeeds even when those variables are undefined.
   */
  private safeTranslateStream(key: string) {
    // Provide safe default values for common variables that might be referenced in translations
    // This prevents "Cannot read properties of undefined" errors during MessageFormat compilation
    const safeDefaults = {
      error: { error: '', message: '' },
      field: {},
      model: {},
      formState: {}
    };

    return this.translate.stream(key, safeDefaults).pipe(
      catchError((error: any) => {
        // If translation still fails, try get() which properly awaits translation availability
        // (instant() returns synchronously and may return the key if translations aren't loaded)
        console.warn(`Translation error for key "${key}":`, error);
        return this.translate.get(key, safeDefaults).pipe(
          catchError(() => of(key))
        );
      })
    );
  }

  prePopulate(field: FormlyFieldConfig) {
    const props = field.props || {};
    if (!props.translate || props._translated) {
      return;
    }
    if (props.label) {
      props._translated = true;
      field.expressions = {
        ...(field.expressions || {}),
        'props.label': this.safeTranslateStream(props.label),
      }
    };
    if (props.placeholder) {
      props._translated = true;
      field.expressions = {
        ...(field.expressions || {}),
        'props.placeholder': this.safeTranslateStream(props.placeholder),
      }
    };
    if (props.hint) {
      props._translated = true;
      field.expressions = {
        ...(field.expressions || {}),
        'props.hint': this.safeTranslateStream(props.hint),
      }
    };
  }
}

export function registerTranslateExtension(translate: TranslateService) {
  // Safe defaults for translation interpolation to prevent "Cannot read properties of undefined" errors
  const safeDefaults = {
    error: { error: '', message: '' },
    field: {},
    model: {},
    formState: {}
  };

  return {
    validationMessages: [
      {
        name: 'required',
        message(error?: any, field?: FormlyFieldConfig) {
          // Ensure error is always defined to prevent interpolation errors
          const safeError = error || { error: '', message: '' };
          return translate.stream(_('form.validation.required'), { ...safeDefaults, error: safeError });
        }
      },
      {
        name: 'url',
        message(error?: any, field?: FormlyFieldConfig) {
          const safeError = error || { error: '', message: '' };
          return translate.stream(_('form.validation.url'), { ...safeDefaults, error: safeError });
        }
      },
      {
        name: 'mail',
        message(error?: any, field?: FormlyFieldConfig) {
          const safeError = error || { error: '', message: '' };
          const key = error?.multipleEmails
            ? _('form.validation.mailSingleOnly')
            : _('form.validation.mail');
          return translate.stream(key, { ...safeDefaults, error: safeError });
        }
      },
      {
        name: 'mails',
        message(error?: any, field?: FormlyFieldConfig) {
          const invalidEmailsList = Array.isArray(error?.invalidEmails)
            ? error.invalidEmails.join(', ')
            : '';
          return translate.stream(_('form.validation.mails'), {
            ...safeDefaults,
            invalidEmailsList
          });
        }
      },
      {
        name: 'phone',
        message(error?: any, field?: FormlyFieldConfig) {
          const safeError = error || { error: '', message: '' };
          return translate.stream(_('form.validation.phone'), { ...safeDefaults, error: safeError });
        }
      }
    ],
    extensions: [{
      name: 'translate',
      extension: new TranslateExtension(translate),
    }],
  };
}
